import { useState, useEffect, useRef } from 'react';
import { Timestamp } from 'firebase/firestore';

interface Message {
  id: string;
  text: string;
  from: string;
  timestamp: Timestamp;
}

interface MessageSectionProps {
  messages: Message[];
  currentPerson: string;
  onSendMessage: (text: string) => void;
  theme: any;
}

const MessageSection = ({ messages, currentPerson, onSendMessage, theme }: MessageSectionProps) => {
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim()) {
      onSendMessage(newMessage);
      setNewMessage('');
    }
  };

  const formatTime = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (diffInHours < 48) {
      return 'Yesterday ' + date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  };

  return (
    <div className={`${theme.secondary} rounded-2xl p-6 shadow-lg`}>
      <h2 className={`text-2xl font-bold ${theme.text} mb-4`}>
        Messages
      </h2>

      {/* Messages Display */}
      <div className="bg-white rounded-xl p-4 mb-4 h-96 overflow-y-auto">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-400 text-center">
              No messages yet. <br />
              Send your first message! 💌
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((message) => {
              const isCurrentUser = message.from === currentPerson;
              return (
                <div
                  key={message.id}
                  className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`
                      max-w-[75%] rounded-2xl px-4 py-2 shadow-sm
                      ${isCurrentUser 
                        ? `bg-gradient-to-r ${theme.primary} text-white` 
                        : 'bg-gray-100 text-gray-800'
                      }
                    `}
                  >
                    <p className="text-sm md:text-base break-words">{message.text}</p>
                    <p 
                      className={`text-xs mt-1 ${
                        isCurrentUser ? 'text-white opacity-70' : 'text-gray-500'
                      }`}
                    >
                      {formatTime(message.timestamp)}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Write a sweet message..."
          className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          maxLength={500}
        />
        <button
          type="submit"
          disabled={!newMessage.trim()}
          className={`
            ${theme.button} text-white px-6 py-3 rounded-lg font-semibold
            transition-all duration-200
            disabled:opacity-50 disabled:cursor-not-allowed
            hover:opacity-90
          `}
        >
          Send 💌
        </button>
      </form>
    </div>
  );
};

export default MessageSection;